import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

export type ThemePreference = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

interface ThemeProviderProps {
  children: ReactNode;
  storageKey: string;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function parsePreference(value: string | null): ThemePreference {
  return value === "light" || value === "dark" ? value : "system";
}

function readPreference(storageKey: string): ThemePreference {
  try {
    return parsePreference(window.localStorage.getItem(storageKey));
  } catch {
    // Storage can be unavailable in an embedded or restricted browser.
    return "system";
  }
}

/** The host application owns the palette, storage key and pre-paint bootstrap. */
export function ThemeProvider({ children, storageKey }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState(() =>
    readPreference(storageKey),
  );
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
  );
  const resolvedTheme =
    preference === "system" ? (systemDark ? "dark" : "light") : preference;

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    function updateSystem() {
      setSystemDark(media?.matches ?? false);
    }
    updateSystem();
    media?.addEventListener("change", updateSystem);
    return () => media?.removeEventListener("change", updateSystem);
  }, []);

  useEffect(() => {
    function syncPreference(event: StorageEvent) {
      try {
        if (event.storageArea !== window.localStorage) return;
      } catch {
        return;
      }
      if (event.key === storageKey || event.key === null) {
        setPreferenceState(parsePreference(event.newValue));
      }
    }
    window.addEventListener("storage", syncPreference);
    return () => window.removeEventListener("storage", syncPreference);
  }, [storageKey]);

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  function setPreference(next: ThemePreference) {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(storageKey, next);
    } catch {
      // Keep the in-memory choice usable even when persistence is blocked.
    }
  }

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Optional so shared components can also be used without application theming. */
export function useTheme() {
  return useContext(ThemeContext);
}
